// Synchronous key/value storage primitive (the `localStorage` shape, narrowed to what we use).
export type StorageAdapter = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

// Session-scoped fallback for when `localStorage` is unavailable (private mode, blocked storage).
export function createMemoryStorageAdapter(): StorageAdapter {
  const store = new Map<string, string>();
  return {
    getItem: (key) => store.get(key) ?? null,
    setItem: (key, value) => {
      store.set(key, value);
    },
    removeItem: (key) => {
      store.delete(key);
    },
  };
}

// Some contexts expose `localStorage` but throw on access, so probe rather than trust presence.
function isLocalStorageAvailable(): boolean {
  try {
    const probe = '__spark_storage_probe__';
    localStorage.setItem(probe, probe);
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

// Real `localStorage`, guarded so a mid-session revocation degrades to a no-op rather than throws.
function createBrowserStorageAdapter(): StorageAdapter {
  return {
    getItem: (key) => {
      try {
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    setItem: (key, value) => {
      try {
        localStorage.setItem(key, value);
      } catch {}
    },
    removeItem: (key) => {
      try {
        localStorage.removeItem(key);
      } catch {}
    },
  };
}

function createLocalStorageAdapter(): StorageAdapter {
  const available = isLocalStorageAvailable();
  // Diagnostic: report once, at adapter init, which backing store we resolved to — an in-memory
  // fallback (e.g. a cross-site iframe that blocks storage) means nothing persists across loads.
  if (typeof window !== 'undefined') {
    console.info(
      available
        ? '[spark-dash storage] localStorage available — persisting to localStorage'
        : '[spark-dash storage] localStorage unavailable — using in-memory fallback (nothing persists across loads)',
    );
  }
  return available ? createBrowserStorageAdapter() : createMemoryStorageAdapter();
}

export const defaultStorageAdapter: StorageAdapter = createLocalStorageAdapter();
